import type { QueryKey } from "@tanstack/react-query";

/**
 * One place that names every query key.
 *
 * The console socket needs to turn a topic name into the queries that topic
 * feeds (for a seq-gap refetch) and a route into the queries that route is
 * showing (for a reconnect refetch). Both are impossible to keep right if keys
 * are spelled inline at each call site.
 */
export const qk = {
  me: ["me"] as const,
  overview: ["overview"] as const,

  devices: (params?: unknown) => (params === undefined ? (["devices"] as const) : (["devices", params] as const)),
  device: (id: string) => ["device", id] as const,
  deviceApps: (id: string) => ["device", id, "apps"] as const,
  deviceLogs: (id: string) => ["device", id, "logs"] as const,

  tasks: () => ["tasks"] as const,
  task: (id: string) => ["task", id] as const,
  taskAssignments: (id: string) => ["task", id, "assignments"] as const,

  runs: (params?: unknown) => (params === undefined ? (["runs"] as const) : (["runs", params] as const)),
  run: (id: string) => ["run", id] as const,
  runSteps: (id: string) => ["run", id, "steps"] as const,

  dispatch: (id: string) => ["dispatch", id] as const,

  groups: ["groups"] as const,
  fleetSettings: ["settings", "fleet"] as const,
};

export type Topic = string;

export const TOPIC_FLEET_SUMMARY = "fleet.summary";
export const topicDevice = (id: string): Topic => `device.${id}`;
export const topicRun = (id: string): Topic => `run.${id}`;
export const topicDispatch = (id: string): Topic => `dispatch.${id}`;
/** Binary screen frames, phase 2. */
export const topicControl = (id: string): Topic => `control.${id}`;

/**
 * The query keys a topic's frames stand in for. A `seq` gap on that topic means
 * the client missed at least one update, so these are refetched — the tail must
 * never be trusted to have been gapless.
 */
export function queriesForTopic(topic: Topic): QueryKey[] {
  if (topic === TOPIC_FLEET_SUMMARY) return [qk.overview, ["devices"]];
  const [kind, ...rest] = topic.split(".");
  const id = rest.join(".");
  switch (kind) {
    case "device":
      return [qk.device(id), ["devices"]];
    case "run":
      return [qk.run(id), qk.runSteps(id)];
    case "dispatch":
      return [qk.dispatch(id)];
    default:
      return [];
  }
}

/**
 * The query keys the given route is actually rendering.
 *
 * On reconnect we refetch only these. Invalidating everything would have four
 * idle tabs each fire a dozen requests the moment a flapping uplink comes back —
 * exactly when the server is least able to absorb it.
 */
export function queriesForRoute(pathname: string): QueryKey[] {
  const seg = pathname.split("/").filter(Boolean);
  const [head, id, tab] = seg;
  switch (head) {
    case undefined:
    case "overview":
      return [qk.overview];
    case "devices":
      if (!id) return [["devices"]];
      if (tab === "logs") return [qk.device(id), qk.deviceLogs(id)];
      return [qk.device(id), ["runs"]];
    case "tasks":
      return id ? [qk.task(id), qk.tasks()] : [qk.tasks()];
    case "dispatch":
      return [qk.tasks(), ["devices"]];
    case "runs":
      if (!id) return [["runs"]];
      return [qk.run(id), qk.runSteps(id)];
    case "settings":
      return [qk.fleetSettings];
    default:
      return [];
  }
}
